import { useState, useEffect } from 'react';
import { getAdminSupportTicket, replyToTicketAdmin, updateTicketStatus, assignTicket } from '../../api/supportTicketApi';
import { fmtIST } from '../../utils/dateFormat';
import { useAdminAuth } from '../../context/AdminAuthContext';

const cyan = '#00e5ff';

const statusColors = {
  open: '#ffaa44',
  in_progress: '#66aaff',
  resolved: '#66ff99',
  closed: '#888',
};

const AdminViewSupportTicket = ({ ticket: initialTicket, onClose }) => {
  const { adminUser } = useAdminAuth();
  const [ticket, setTicket] = useState(initialTicket);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [newStatus, setNewStatus] = useState(initialTicket.status);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    getAdminSupportTicket(initialTicket._id)
      .then((res) => {
        setTicket(res.data.ticket);
        setMessages(res.data.messages);
        setNewStatus(res.data.ticket.status);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load ticket'))
      .finally(() => setLoading(false));
  }, [initialTicket._id]);

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setReplying(true);
    setError('');
    try {
      await replyToTicketAdmin(ticket._id, { message: replyText });
      setReplyText('');
      const res = await getAdminSupportTicket(ticket._id);
      setTicket(res.data.ticket);
      setMessages(res.data.messages);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async () => {
    if (newStatus === ticket.status) return;

    setUpdating(true);
    setError('');
    try {
      const res = await updateTicketStatus(ticket._id, { status: newStatus });
      setTicket(res.data.ticket);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
      setNewStatus(ticket.status);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        overflow: 'auto',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#111',
          border: '1px solid rgba(0,229,255,0.2)',
          borderRadius: 12,
          maxWidth: 900,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'grid',
          gridTemplateColumns: '1fr 280px',
          gap: 0,
        }}
      >
        {/* Main Content */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ borderBottom: '1px solid rgba(0,229,255,0.1)', padding: '20px', background: 'rgba(0,229,255,0.03)' }}>
            <div style={{ color: cyan, fontSize: 11, fontWeight: 600, fontFamily: 'monospace', marginBottom: 4 }}>
              {ticket.ticketId}
            </div>
            <h4 style={{ color: '#fff', fontWeight: 700, margin: 0, marginBottom: 4 }}>{ticket.title}</h4>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>{ticket.description}</div>
            <div style={{ display: 'flex', gap: 20, fontSize: 11, color: '#555' }}>
              <div>Created: {fmtIST(ticket.createdAt)}</div>
              <div>Category: <span style={{ color: '#888' }}>{ticket.category}</span></div>
            </div>
          </div>

          {/* Messages */}
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#555', flex: 1 }}>Loading...</div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {messages.length === 0 ? (
                <div style={{ color: '#555', textAlign: 'center', padding: 20 }}>No messages yet</div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg._id}
                    style={{
                      background: msg.senderType === 'admin' ? 'rgba(102,255,153,0.08)' : 'rgba(0,229,255,0.08)',
                      border: `1px solid ${msg.senderType === 'admin' ? 'rgba(102,255,153,0.2)' : 'rgba(0,229,255,0.2)'}`,
                      borderRadius: 8,
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: msg.senderType === 'admin' ? '#66ff99' : cyan }}>
                        {msg.senderId.name} {msg.senderType === 'admin' && <span style={{ fontSize: 10, color: '#666' }}>(Admin)</span>} {msg.senderId._id === adminUser?.id && '(You)'}
                      </div>
                      <div style={{ fontSize: 10, color: '#555' }}>{fmtIST(msg.createdAt)}</div>
                    </div>
                    <div style={{ color: '#ccc', fontSize: 13, lineHeight: '1.5', wordBreak: 'break-word' }}>{msg.message}</div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Reply Box */}
          <div style={{ borderTop: '1px solid rgba(0,229,255,0.1)', padding: '16px' }}>
            {error && (
              <div style={{ background: 'rgba(255,100,100,0.1)', border: '1px solid rgba(255,100,100,0.3)', color: '#ff6666', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type admin reply..."
                rows="3"
                disabled={replying}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(0,229,255,0.2)',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>

            <button
              onClick={handleReply}
              disabled={replying || !replyText.trim()}
              style={{
                width: '100%',
                background: replying || !replyText.trim() ? 'rgba(0,229,255,0.3)' : cyan,
                border: 'none',
                color: '#000',
                borderRadius: 8,
                padding: '8px',
                fontWeight: 600,
                fontSize: 12,
                cursor: replying || !replyText.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              {replying ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ borderLeft: '1px solid rgba(0,229,255,0.1)', padding: '16px', display: 'flex', flexDirection: 'column', gap: 16, background: 'rgba(0,229,255,0.02)', overflowY: 'auto' }}>
          {/* User Details */}
          <div>
            <div style={{ color: '#888', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>User</div>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{ticket.userId.name}</div>
            <div style={{ color: '#888', fontSize: 11, marginBottom: 2 }}>{ticket.userId.email}</div>
            <div style={{ color: cyan, fontSize: 11, fontFamily: 'monospace', fontWeight: 600 }}>{ticket.userId.userCode}</div>
            <div style={{ color: '#888', fontSize: 10, marginTop: 6 }}>Mobile: {ticket.userId.mobile}</div>
            <div style={{ color: '#888', fontSize: 10 }}>Country: {ticket.userId.country || '—'}</div>
            <div style={{ color: ticket.userId.isActive ? '#66ff99' : '#ff6666', fontSize: 10, marginTop: 4, fontWeight: 600 }}>
              {ticket.userId.isActive ? '✓ Active' : '✗ Inactive'}
            </div>
          </div>

          {/* Status */}
          <div>
            <div style={{ color: '#888', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Status</div>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={updating}
              style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid rgba(0,229,255,0.2)',
                borderRadius: 6,
                padding: '8px',
                color: '#fff',
                fontSize: 12,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            {newStatus !== ticket.status && (
              <button
                onClick={handleStatusChange}
                disabled={updating}
                style={{
                  width: '100%',
                  background: cyan,
                  border: 'none',
                  color: '#000',
                  borderRadius: 6,
                  padding: '6px',
                  fontWeight: 600,
                  fontSize: 11,
                  cursor: 'pointer',
                  marginTop: 6,
                }}
              >
                {updating ? 'Updating...' : 'Update'}
              </button>
            )}
          </div>

          {/* Category */}
          <div>
            <div style={{ color: '#888', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Category</div>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{ticket.category}</div>
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: 'none',
              color: '#888',
              borderRadius: 6,
              padding: '8px',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
              marginTop: 'auto',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminViewSupportTicket;
